import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { captureUTMParams } from "./utils/utm";
import { captureRecinto } from "./utils/recinto";
import { capturePreset } from "./utils/presets";

// Code-splitting por ruta: cada página es un chunk aparte, la landing no carga
// el wizard ni las páginas de pago/progreso hasta que se navegan.
const LandingPage = lazy(() => import("./pages/LandingPage"));
const CotizadorWizardPage = lazy(() =>
  import("./pages/CotizadorWizardPage").then((m) => ({ default: m.CotizadorWizardPage }))
);
// Puerta reCAPTCHA del wizard en su propio chunk: la landing no descarga reCAPTCHA.
const CaptchaGate = lazy(() => import("./components/acceso/CaptchaGate"));
const AceptarCotizacionPage = lazy(() => import("./pages/AceptarCotizacionPage"));
const ProgresoProyectoPage = lazy(() => import("./pages/ProgresoProyectoPage"));
const PagoPage = lazy(() => import("./pages/PagoPage"));

function RouteFallback() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: T.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={28} sx={{ color: T.accent }} />
    </Box>
  );
}

/**
 * Red de seguridad del code-splitting.
 *
 * Con las rutas en chunks separados, cada navegación descarga un
 * `assets/chunk.<hash>.js`. El pipeline reconstruye la imagen con hashes
 * nuevos y nginx responde 404 a los viejos, así que quien tenga la pestaña
 * abierta durante un deploy recibe 404 al navegar: el import() rechaza, el
 * Suspense nunca resuelve y la página queda EN BLANCO sin ningún error visible.
 *
 * Ante ese caso concreto se recarga una vez (la recarga trae el index.html
 * nuevo con los hashes correctos). El flag en sessionStorage evita el bucle si
 * la recarga no lo soluciona; cualquier otro error se muestra, no se oculta.
 */
const RECARGA_FLAG = "chunk_reload_intentado";

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fallo: false };
  }

  static getDerivedStateFromError() {
    return { fallo: true };
  }

  componentDidCatch(error) {
    const esChunk =
      error?.name === "ChunkLoadError" ||
      /Loading chunk|dynamically imported module|Failed to fetch/i.test(error?.message || "");
    if (esChunk && !sessionStorage.getItem(RECARGA_FLAG)) {
      sessionStorage.setItem(RECARGA_FLAG, "1");
      window.location.reload();
      return;
    }
    console.error("Error al cargar la vista:", error);
  }

  render() {
    if (!this.state.fallo) return this.props.children;
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: T.bg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 3,
          textAlign: "center",
          color: T.textMuted,
        }}
      >
        <Box sx={{ color: T.textPrimary, fontSize: "1.1rem" }}>
          No pudimos cargar esta sección
        </Box>
        <Box sx={{ fontSize: "0.85rem" }}>
          Puede haber una versión nueva del sitio. Recarga la página para continuar.
        </Box>
        <Box
          onClick={() => {
            sessionStorage.removeItem(RECARGA_FLAG);
            window.location.reload();
          }}
          sx={{
            mt: 1,
            px: 3,
            py: 1,
            borderRadius: "6px",
            border: `1px solid ${T.border}`,
            color: T.accent,
            cursor: "pointer",
          }}
        >
          Recargar
        </Box>
      </Box>
    );
  }
}

export default function App() {
  // Captura los params de deep-link (?venue=, ?recinto=, UTMs) en CUALQUIER
  // ruta de aterrizaje y los persiste en sessionStorage. Sin esto, llegar a
  // /?venue=esh y navegar a /cotizador por un CTA (client-side) pierde los
  // params — el wizard nunca vería el preset. No aplica nada al formulario
  // aquí; solo persiste para que el wizard lo consuma al montar.
  useEffect(() => {
    captureUTMParams();
    captureRecinto();
    capturePreset();
  }, []);

  return (
    <BrowserRouter>
      <ChunkErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* Puerta reCAPTCHA SOLO en el wizard: aceptar/pago/progreso ya
                van protegidos por su token de capacidad en la URL. */}
            <Route
              path="/cotizador"
              element={
                <CaptchaGate>
                  <CotizadorWizardPage />
                </CaptchaGate>
              }
            />
            <Route path="/cotizador/aceptar/:token" element={<AceptarCotizacionPage />} />
            <Route path="/cotizador/progreso/:token" element={<ProgresoProyectoPage />} />
            <Route path="/cotizador/pago/:token" element={<PagoPage />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </BrowserRouter>
  );
}
